import { ACTIVITY_STATUS_LABEL, AUTHORITY_LABEL, formatImpact, type ActivityRecord, type ActivityStats } from "@ti/core";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Activity, AlertTriangle, ArrowUpRight, CheckCircle2, ChevronLeft, ChevronRight, MessageSquareWarning, ShieldOff, UserRoundX, X, XCircle } from "lucide-react";
import { useMemo, useState } from "react";
import { AgentLink, ImpactList, PageHeader, TimeAgo } from "@/components/domain/misc";
import { StatTile } from "@/components/domain/stat";
import { ActivityStatusChip, AuthorityChip, ControlChip } from "@/components/domain/status";
import { ErrorPanel, Page } from "@/components/layout/page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { type ColumnDef, DataTable } from "@/components/ui/data-table";
import { Select } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/misc";
import { Tooltip } from "@/components/ui/tooltip";
import { ActivityByClass, DailyActivityChart } from "@/features/activity/charts";
import { KindIcon } from "@/features/activity/kind-icon";
import { ActivitySheet } from "@/features/activity/ledger-sheet";
import {
  ACTIVITY_KINDS,
  ACTIVITY_STATUSES,
  AFFECTED_KIND_LABEL,
  AUTHORITY_VERDICTS,
  type ActivitySearch,
  FILTER_KEYS,
  KIND_LABEL,
  PAGE_SIZE,
  parseActivitySearch,
} from "@/features/activity/lib";
import { q } from "@/lib/api";
import { fmtInt, fmtPct } from "@/lib/format";
import { usePackName } from "@/lib/packs";

export const Route = createFileRoute("/_app/activity")({
  validateSearch: parseActivitySearch,
  component: ActivityPage,
});

function ActivityPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const setSearch = (patch: Partial<ActivitySearch>) =>
    void navigate({ search: (prev: ActivitySearch) => Object.fromEntries(Object.entries({ ...prev, ...patch }).filter(([, v]) => v !== undefined && v !== "")) as ActivitySearch, replace: true });

  const statsQ = useQuery(q.activityStats({ agentId: search.agent }));
  const agentsQ = useQuery(q.agents());
  const packsQ = useQuery(q.packs());
  const packName = usePackName();
  const optionalPacks = (packsQ.data ?? []).filter((p) => !p.core);

  // Recently affected parties for the filter, derived from the latest activity (parties are identified per session).
  const recentQ = useQuery(q.activity({ agentId: search.agent, limit: 200 }));
  const recentAffected = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of recentQ.data?.items ?? []) if (r.affected?.id && !seen.has(r.affected.id)) seen.set(r.affected.id, r.affected.label);
    return [...seen].slice(0, 40).map(([id, label]) => ({ id, label }));
  }, [recentQ.data]);
  const classes = useMemo(() => [...(statsQ.data?.byClass ?? [])].sort((a, b) => a.actionClass.localeCompare(b.actionClass)).map((c) => c.actionClass), [statsQ.data]);
  const hasFilters = FILTER_KEYS.some((k) => search[k] !== undefined);
  const selectedAffected = recentAffected.find((u) => u.id === search.affectedId)?.label;

  return (
    <Page>
      <PageHeader
        title="Activity"
        description="Everything your agents did and said — every side-effecting action and every outward statement, with who it affected, its impact, and whether it was within the agent's authority. This ledger is the evidence behind issues and claims."
      />

      {statsQ.error ? <ErrorPanel error={statsQ.error} reset={() => void statsQ.refetch()} className="mb-4" /> : <KpiStrip stats={statsQ.data} onFilter={setSearch} />}

      <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <Card>
          <CardHeader title="Daily activity" description="Last 7 days, by authority and outcome" />
          <CardBody>{statsQ.data ? <DailyActivityChart data={statsQ.data.daily} /> : <Skeleton className="h-[248px]" />}</CardBody>
        </Card>
        <Card>
          <CardHeader title="By action class" description="Last 7 days · click to filter" />
          <CardBody>
            {statsQ.data ? (
              <ActivityByClass data={statsQ.data.byClass} selected={search.actionClass} onSelect={(c) => setSearch({ actionClass: search.actionClass === c ? undefined : c })} />
            ) : (
              <Skeleton className="h-[248px]" />
            )}
          </CardBody>
        </Card>
      </div>

      <section aria-labelledby="ledger-title" className="mt-6">
        <h2 id="ledger-title" className="mb-2 text-[13px] font-semibold">
          Ledger
        </h2>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Select aria-label="Agent" value={search.agent ?? ""} onChange={(e) => setSearch({ agent: e.target.value || undefined })} className="w-40">
            <option value="">All agents</option>
            {agentsQ.data?.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Select>
          <Select aria-label="Kind" value={search.kind ?? ""} onChange={(e) => setSearch({ kind: (e.target.value || undefined) as ActivitySearch["kind"] })} className="w-44">
            <option value="">Actions & statements</option>
            {ACTIVITY_KINDS.map((k) => (
              <option key={k} value={k}>
                {KIND_LABEL[k]}
              </option>
            ))}
          </Select>
          <Select aria-label="Authority" value={search.authority ?? ""} onChange={(e) => setSearch({ authority: (e.target.value || undefined) as ActivitySearch["authority"] })} className="w-40">
            <option value="">Any authority</option>
            {AUTHORITY_VERDICTS.map((v) => (
              <option key={v} value={v}>
                {AUTHORITY_LABEL[v]}
              </option>
            ))}
          </Select>
          <Select aria-label="Status" value={search.status ?? ""} onChange={(e) => setSearch({ status: (e.target.value || undefined) as ActivitySearch["status"] })} className="w-32">
            <option value="">All statuses</option>
            {ACTIVITY_STATUSES.map((s) => (
              <option key={s} value={s}>
                {ACTIVITY_STATUS_LABEL[s]}
              </option>
            ))}
          </Select>
          <Select aria-label="Action class" value={search.actionClass ?? ""} onChange={(e) => setSearch({ actionClass: e.target.value || undefined })} className="w-44 font-mono text-[12px]">
            <option value="">All action classes</option>
            {search.actionClass && !classes.includes(search.actionClass) ? <option value={search.actionClass}>{search.actionClass}</option> : null}
            {classes.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
          {optionalPacks.length ? (
            <Select aria-label="Pack" value={search.pack ?? ""} onChange={(e) => setSearch({ pack: e.target.value || undefined })} className="w-36">
              <option value="">Any pack</option>
              {optionalPacks.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
          ) : null}
          <Select aria-label="Affected party" value={search.affectedId ?? ""} onChange={(e) => setSearch({ affectedId: e.target.value || undefined })} className="w-44">
            <option value="">Anyone affected</option>
            {search.affectedId && !selectedAffected ? <option value={search.affectedId}>{search.affectedId}</option> : null}
            {recentAffected.map((u) => (
              <option key={u.id} value={u.id}>
                {u.label} · {u.id}
              </option>
            ))}
          </Select>
          {hasFilters ? (
            <Button variant="ghost" size="sm" onClick={() => setSearch(Object.fromEntries(FILTER_KEYS.map((k) => [k, undefined])))}>
              <X /> Clear filters
            </Button>
          ) : null}
        </div>

        {search.affectedId ? <AffectedSummary affectedId={search.affectedId} agentId={search.agent} /> : null}

        {/* Remount on filter change so pagination restarts at the first page. */}
        <LedgerTable key={FILTER_KEYS.map((k) => search[k] ?? "").join("|")} search={search} hasFilters={hasFilters} packName={packName} onFilter={setSearch} />
      </section>
    </Page>
  );
}

// ─── KPIs ────────────────────────────────────────────────────────────────────

function KpiStrip({ stats, onFilter }: { stats: ActivityStats | undefined; onFilter: (p: Partial<ActivitySearch>) => void }) {
  if (!stats) {
    return (
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        {Array.from({ length: 5 }, (_, i) => (
          <Skeleton key={i} className="h-[104px]" />
        ))}
      </div>
    );
  }
  const outShare = stats.total7d ? stats.outOfPolicy7d / stats.total7d : 0;
  const top = [...stats.byClass].sort((a, b) => b.count - a.count)[0];
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
      <StatTile label="Activity · 7d" icon={<Activity />} value={fmtInt(stats.total7d)} sub={top ? <>Most common: <span className="font-mono text-[11.5px]">{top.actionClass}</span></> : "Actions and statements"} />
      <button type="button" className="cursor-pointer rounded-lg text-left" onClick={() => onFilter({ authority: "outside" })} aria-label="Filter to out-of-policy activity">
        <StatTile label="Out of policy · 7d" icon={<ShieldOff />} value={fmtInt(stats.outOfPolicy7d)} sub={`${fmtPct(outShare, 1)} outside the authority profile`} className="h-full" />
      </button>
      <StatTile
        label="Out-of-policy impact · 7d"
        icon={<AlertTriangle />}
        value={stats.outOfPolicyImpact7d.length ? formatImpact(stats.outOfPolicyImpact7d[0], { compact: true }) : "—"}
        sub={
          stats.outOfPolicyImpact7d.length > 1 ? (
            <ImpactList impact={stats.outOfPolicyImpact7d.slice(1)} compact className="text-fg-3" />
          ) : (
            "Measured in each action's own units"
          )
        }
      />
      <button type="button" className="cursor-pointer rounded-lg text-left" onClick={() => onFilter({ status: "failed" })} aria-label="Filter to failed activity">
        <StatTile label="Failed · 7d" icon={<XCircle />} value={fmtInt(stats.failed7d)} sub="Errored or never carried out" className="h-full" />
      </button>
      <button type="button" className="cursor-pointer rounded-lg text-left" onClick={() => onFilter({ kind: "statement" })} aria-label="Filter to statements">
        <StatTile label="Ungrounded statements" icon={<MessageSquareWarning />} value={fmtInt(stats.ungroundedStatements7d)} sub="Not backed by a tool result or document" className="h-full" />
      </button>
    </div>
  );
}

// ─── Per-affected-party view ─────────────────────────────────────────────────

function AffectedSummary({ affectedId, agentId }: { affectedId: string; agentId: string | undefined }) {
  const { data, isPending } = useQuery(q.activity({ affectedId, agentId, limit: 500 }));
  const summary = useMemo(() => {
    const items = data?.items ?? [];
    const outside = items.filter((r) => r.authority === "outside");
    return {
      total: data?.total ?? 0,
      outside: outside.length,
      outsideImpact: outside.map((r) => r.impact),
      statements: items.filter((r) => r.kind === "statement").length,
      ungrounded: items.filter((r) => r.grounded === false).length,
      failed: items.filter((r) => r.status === "failed").length,
    };
  }, [data]);
  const party = data?.items[0]?.affected;

  return (
    <Card className="mb-3 flex flex-wrap items-center gap-x-8 gap-y-3 px-4 py-3">
      <div className="min-w-0">
        <div className="text-[13.5px] font-semibold">{party?.label ?? affectedId}</div>
        <div className="text-xs text-fg-3">
          <span className="font-mono text-[11.5px]">{affectedId}</span>
          {party ? ` · ${AFFECTED_KIND_LABEL[party.kind].toLowerCase()}` : null}
          {agentId ? " · filtered to one agent" : null}
        </div>
      </div>
      {isPending ? (
        <Skeleton className="h-9 w-80" />
      ) : (
        <dl className="flex flex-wrap gap-x-7 gap-y-2">
          <Metric label="Activity" value={fmtInt(summary.total)} />
          <Metric
            label="Outside authority"
            value={
              <>
                {fmtInt(summary.outside)}
                {summary.outside ? (
                  <span className="ml-1.5 text-[13px] font-normal text-fg-2">
                    · <ImpactList impact={summary.outsideImpact.filter((i) => i !== null)} compact />
                  </span>
                ) : null}
              </>
            }
          />
          <Metric label="Statements" value={fmtInt(summary.statements)} />
          <Metric label="Ungrounded" value={fmtInt(summary.ungrounded)} />
          <Metric label="Failed" value={fmtInt(summary.failed)} />
        </dl>
      )}
    </Card>
  );
}

function Metric({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[11px] text-fg-3">{label}</dt>
      <dd className="text-[15px] font-semibold tabular">{value}</dd>
    </div>
  );
}

// ─── Ledger table ────────────────────────────────────────────────────────────

function LedgerTable({ search, hasFilters, packName, onFilter }: { search: ActivitySearch; hasFilters: boolean; packName: (id: string) => string; onFilter: (p: Partial<ActivitySearch>) => void }) {
  const [cursors, setCursors] = useState<Array<string | undefined>>([undefined]);
  const cursor = cursors[cursors.length - 1];
  const page = cursors.length;
  const query = useQuery(
    q.activity({
      agentId: search.agent,
      kind: search.kind,
      authority: search.authority,
      status: search.status,
      actionClass: search.actionClass,
      pack: search.pack,
      affectedId: search.affectedId,
      limit: PAGE_SIZE,
      cursor,
    }),
  );
  const [selected, setSelected] = useState<ActivityRecord | null>(null);

  // Server-paginated: sorting one page would mislead, so the ledger stays in time order (newest first).
  const columns = useMemo<ColumnDef<ActivityRecord>[]>(
    () =>
      ([
        {
          id: "ts",
          header: "Time",
          accessorKey: "ts",
          cell: ({ row }) => <TimeAgo iso={row.original.ts} className="text-xs" />,
        },
        {
          id: "agent",
          header: "Agent",
          accessorKey: "agentName",
          cell: ({ row }) => <AgentLink id={row.original.agentId} name={row.original.agentName} className="whitespace-nowrap text-[12px]" />,
        },
        {
          id: "what",
          header: "What",
          accessorKey: "summary",
          cell: ({ row }) => {
            const r = row.original;
            return (
              <div className="flex min-w-[240px] max-w-[380px] items-start gap-2">
                <Tooltip content={r.kind === "action" ? "Action — something the agent did" : "Statement — something the agent said outward"}>
                  <span className="mt-0.5 shrink-0 text-fg-3">
                    <KindIcon kind={r.kind} className="size-3.5" />
                  </span>
                </Tooltip>
                <div className="min-w-0 leading-tight">
                  <div className="truncate text-fg" title={r.summary}>
                    {r.summary}
                  </div>
                  <div className="mt-0.5 flex min-w-0 items-center gap-1.5">
                    <span className="truncate font-mono text-[11px] text-fg-3">{r.actionClass}</span>
                    {r.detail ? (
                      <Badge tone="violet" className="shrink-0" title={`${packName(r.detail.pack)} pack`}>
                        {r.detail.label}
                      </Badge>
                    ) : null}
                    {r.provoked ? (
                      <Tooltip content="Induced by manipulation from the affected party">
                        <span className="shrink-0 text-warning-text">
                          <UserRoundX className="size-3" aria-label="Provoked" />
                        </span>
                      </Tooltip>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          },
        },
        {
          id: "affected",
          header: "Affected",
          accessorFn: (r) => r.affected?.label ?? "",
          cell: ({ row }) => {
            const a = row.original.affected;
            if (!a) return <span className="text-fg-3">—</span>;
            const id = a.id;
            return (
              <div className="flex flex-col leading-tight">
                {id ? (
                  <Tooltip content={`Show all activity affecting ${a.label}`}>
                    <button
                      type="button"
                      className="max-w-[160px] cursor-pointer self-start truncate text-left text-fg hover:text-accent hover:underline"
                      onClick={(e) => {
                        e.stopPropagation();
                        onFilter({ affectedId: id });
                      }}
                    >
                      {a.label}
                    </button>
                  </Tooltip>
                ) : (
                  <span className="max-w-[160px] truncate text-fg-2">{a.label}</span>
                )}
                <span className="whitespace-nowrap text-[11px] text-fg-3">{AFFECTED_KIND_LABEL[a.kind]}</span>
              </div>
            );
          },
        },
        {
          id: "impact",
          header: "Impact",
          accessorFn: (r) => r.impact?.amount ?? -1,
          meta: { align: "right" },
          cell: ({ row }) => <ImpactList impact={row.original.impact} className="justify-end" />,
        },
        {
          id: "authority",
          header: "Authority",
          accessorKey: "authority",
          cell: ({ row }) =>
            row.original.authorityRule ? (
              <Tooltip content={<span className="block max-w-xs">{row.original.authorityRule}</span>}>
                <span>
                  <AuthorityChip verdict={row.original.authority} short />
                </span>
              </Tooltip>
            ) : (
              <AuthorityChip verdict={row.original.authority} short />
            ),
        },
        {
          id: "status",
          header: "Status",
          accessorKey: "status",
          cell: ({ row }) => <ActivityStatusChip status={row.original.status} />,
        },
        {
          id: "control",
          header: () => (
            <Tooltip content="Control outcome reported by the platform that executes the tool.">
              <span>Control</span>
            </Tooltip>
          ),
          accessorFn: (r) => r.controlOutcome ?? "",
          cell: ({ row }) => (row.original.controlOutcome ? <ControlChip outcome={row.original.controlOutcome} withHelp={false} /> : <span className="text-fg-3">—</span>),
        },
        {
          id: "grounded",
          header: "Grounded",
          accessorFn: (r) => (r.grounded ? 1 : 0),
          cell: ({ row }) => {
            const r = row.original;
            if (r.grounded === null) return <span className="text-fg-3">—</span>;
            return r.grounded ? (
              <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-fg-2">
                <CheckCircle2 className="size-3.5 text-good-text" aria-hidden /> Yes
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-critical-text">
                <XCircle className="size-3.5" aria-hidden /> No
              </span>
            );
          },
        },
        {
          id: "trace",
          header: () => <span className="sr-only">Trace</span>,
          enableSorting: false,
          cell: ({ row }) => (
            <Link
              to="/traces/$traceId"
              params={{ traceId: row.original.traceId }}
              onClick={(e) => e.stopPropagation()}
              aria-label={`Open trace ${row.original.traceId}`}
              className="inline-flex items-center rounded p-1 text-fg-3 hover:bg-surface-3 hover:text-accent"
            >
              <ArrowUpRight className="size-3.5" />
            </Link>
          ),
        },
      ] satisfies ColumnDef<ActivityRecord>[]).map((c) => ({ ...c, enableSorting: false })),
    [onFilter, packName],
  );

  const total = query.data?.total ?? 0;
  const start = (page - 1) * PAGE_SIZE;
  const count = query.data?.items.length ?? 0;

  if (query.error) return <ErrorPanel error={query.error} reset={() => void query.refetch()} />;

  return (
    <>
      <Card className="p-0">
        <DataTable
          data={query.data?.items}
          columns={columns}
          loading={query.isPending}
          dense
          getRowId={(r) => r.id}
          onRowClick={setSelected}
          empty={
            hasFilters
              ? { title: "No activity matches these filters", description: "Try another filter or clear them.", icon: <Activity /> }
              : { title: "No activity recorded yet", description: "Activity appears once an instrumented agent takes a side-effecting action or makes an outward statement.", icon: <Activity /> }
          }
          footer={
            total > 0 ? (
              <div className="flex items-center justify-between gap-3 border-t px-3 py-2 text-xs text-fg-3">
                <span className="tabular" aria-live="polite">
                  {fmtInt(start + 1)}–{fmtInt(start + count)} of {fmtInt(total)}
                  {query.isFetching && !query.isPending ? " · updating…" : null}
                </span>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setCursors((c) => c.slice(0, -1))}>
                    <ChevronLeft /> Previous
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={!query.data?.nextCursor || query.isFetching}
                    onClick={() => {
                      const next = query.data?.nextCursor;
                      if (next) setCursors((c) => [...c, next]);
                    }}
                  >
                    Next <ChevronRight />
                  </Button>
                </div>
              </div>
            ) : null
          }
        />
      </Card>
      <ActivitySheet
        record={selected}
        packName={packName}
        onOpenChange={(o) => !o && setSelected(null)}
        onFilterAffected={(id) => {
          setSelected(null);
          onFilter({ affectedId: id });
        }}
      />
    </>
  );
}
